export enum ChannelType {
  WebWidget = "WebWidget",
  Email = "Email",
  Whatsapp = "Whatsapp",
  Api = "Api",
  Telegram = "Telegram",
  Instagram = "Instagram",
  Messenger = "Messenger",
}

export enum ConversationStatus {
  Open = "Open",
  Pending = "Pending",
  WaitingForOperator = "WaitingForOperator",
  InProgress = "InProgress",
  Resolved = "Resolved",
  Reopened = "Reopened",
  Captured = "Captured",
  Snoozed = "Snoozed",
}

const LEGACY_CONVERSATION_STATUS_MAP: Record<string, ConversationStatus> = {
  AguardandoResponsavel: ConversationStatus.WaitingForOperator,
  EmAtendimento: ConversationStatus.InProgress,
  Reaberta: ConversationStatus.Reopened,
  Capturada: ConversationStatus.Captured,
};

export function normalizeConversationStatus(status: string): ConversationStatus {
  if (Object.values(ConversationStatus).includes(status as ConversationStatus))
  {
    return status as ConversationStatus;
  }

  const legacyStatus = LEGACY_CONVERSATION_STATUS_MAP[status];
  if (legacyStatus)
  {
    return legacyStatus;
  }

  return status as ConversationStatus;
}

export function isConversationClosedForMessaging(status: ConversationStatus): boolean {
  return status === ConversationStatus.Resolved
    || status === ConversationStatus.Captured;
}

export enum ConversationActivityType {
  Created = "Created",
  MessageReceived = "MessageReceived",
  StatusChanged = "StatusChanged",
  TagApplied = "TagApplied",
  TagRemoved = "TagRemoved",
  GroupTransferred = "GroupTransferred",
  OperatorAssumed = "OperatorAssumed",
  AiRedirected = "AiRedirected",
  Finalized = "Finalized",
  SentToBTalk = "SentToBTalk",
  LeadQualificationNotified = "LeadQualificationNotified",
}

export enum ConversationPriority {
  Low = "Low",
  Medium = "Medium",
  High = "High",
  Urgent = "Urgent",
}

export enum ConversationCloseReason {
  Operator = "Operator",
  AutoInactivity = "AutoInactivity",
  Bot = "Bot",
  Visitor = "Visitor",
}

export enum ConversationClassificationStatus {
  Pending = "Pending",
  Processing = "Processing",
  Completed = "Completed",
  Failed = "Failed",
  Skipped = "Skipped",
}

export enum SupportMessageType {
  Incoming = "Incoming",
  Outgoing = "Outgoing",
  Activity = "Activity",
  Template = "Template",
}

export enum SupportSenderType {
  Contact = "Contact",
  AppUser = "AppUser",
  Bot = "Bot",
  System = "System",
}

export enum SupportMessageStatus {
  Pending = "Pending",
  Sent = "Sent",
  Delivered = "Delivered",
  Read = "Read",
  Failed = "Failed",
}

export enum AgentBotType {
  KnowCopilot = "KnowChatbot",
  Webhook = "Webhook",
}

export const CHANNEL_TYPE_LABELS: Record<ChannelType, string> = {
  [ChannelType.WebWidget]: 'Widget Web',
  [ChannelType.Email]: 'E-mail',
  [ChannelType.Whatsapp]: 'WhatsApp',
  [ChannelType.Api]: 'API',
  [ChannelType.Telegram]: 'Telegram',
  [ChannelType.Instagram]: 'Instagram',
  [ChannelType.Messenger]: 'Facebook Messenger',
};

export const defaultEnterSendsMessage = (channelType: ChannelType): boolean => {
  return channelType !== ChannelType.Email;
};

export const CONVERSATION_STATUS_LABELS: Record<ConversationStatus, string> = {
  [ConversationStatus.Open]: 'Aberta',
  [ConversationStatus.Pending]: 'Pendente',
  [ConversationStatus.WaitingForOperator]: 'Aguardando operador',
  [ConversationStatus.InProgress]: 'Em atendimento',
  [ConversationStatus.Resolved]: 'Finalizada',
  [ConversationStatus.Reopened]: 'Reaberta',
  [ConversationStatus.Captured]: 'Capturada',
  [ConversationStatus.Snoozed]: 'Adiada',
};

export const CONVERSATION_STATUS_COLORS: Record<ConversationStatus, string> = {
  [ConversationStatus.Open]: 'bg-green-100 text-green-800',
  [ConversationStatus.Pending]: 'bg-yellow-100 text-yellow-800',
  [ConversationStatus.WaitingForOperator]: 'bg-orange-100 text-orange-800',
  [ConversationStatus.InProgress]: 'bg-blue-100 text-blue-800',
  [ConversationStatus.Resolved]: 'bg-gray-100 text-gray-800',
  [ConversationStatus.Reopened]: 'bg-indigo-100 text-indigo-800',
  [ConversationStatus.Captured]: 'bg-purple-100 text-purple-800',
  [ConversationStatus.Snoozed]: 'bg-slate-100 text-slate-800',
};

export const CONVERSATION_ACTIVITY_LABELS: Record<ConversationActivityType, string> = {
  [ConversationActivityType.Created]: 'Conversa criada',
  [ConversationActivityType.MessageReceived]: 'Mensagem recebida',
  [ConversationActivityType.StatusChanged]: 'Status alterado',
  [ConversationActivityType.TagApplied]: 'Tag aplicada',
  [ConversationActivityType.TagRemoved]: 'Tag removida',
  [ConversationActivityType.GroupTransferred]: 'Transferência de grupo',
  [ConversationActivityType.OperatorAssumed]: 'Operador assumiu',
  [ConversationActivityType.AiRedirected]: 'Redirecionamento por IA',
  [ConversationActivityType.Finalized]: 'Finalizada',
  [ConversationActivityType.SentToBTalk]: 'Enviada ao bTalk',
  [ConversationActivityType.LeadQualificationNotified]: 'Alerta de lead enviado',
};

export const SENDER_TYPE_LABELS: Record<SupportSenderType, string> = {
  [SupportSenderType.Contact]: 'Contato',
  [SupportSenderType.AppUser]: 'Agente',
  [SupportSenderType.Bot]: 'Bot',
  [SupportSenderType.System]: 'Sistema',
};

export const AGENT_BOT_TYPE_LABELS: Record<AgentBotType, string> = {
  [AgentBotType.KnowCopilot]: 'Interno (Know Copilot)',
  [AgentBotType.Webhook]: 'Webhook',
};
